/**
 * Copyright Highway9 Networks Inc.
 *
 * Global fetch interceptor hooks used by `configureFetch`. Every `fetch` in the
 * app runs through these when the real network path is used (not the local fake backend).
 */

import { authenticationService } from "~/services";

/** Mutate or replace the outgoing request before it hits `realFetch`. */
export type FetchRequestInterceptor = (
  url: RequestInfo | URL,
  init: RequestInit | undefined
) =>
  | void
  | { url: RequestInfo | URL; init?: RequestInit }
  | Promise<void | { url: RequestInfo | URL; init?: RequestInit }>;

/**
 * Inspect or wrap successful (`response.ok`) responses after the network round-trip.
 * Error responses skip this chain so the body is still available for 401 handling.
 */
export type FetchResponseInterceptor = (
  response: Response
) => Response | Promise<Response>;

export const requestInterceptors: FetchRequestInterceptor[] = [];

export const responseInterceptors: FetchResponseInterceptor[] = [];

export function registerRequestInterceptor(interceptor: FetchRequestInterceptor): void {
  requestInterceptors.push(interceptor);
}

export function registerResponseInterceptor(interceptor: FetchResponseInterceptor): void {
  responseInterceptors.push(interceptor);
}

export async function applyRequestInterceptors(
  url: RequestInfo | URL,
  init: RequestInit | undefined
): Promise<[RequestInfo | URL, RequestInit | undefined]> {
  if (requestInterceptors.length === 0) {
    return [url, init];
  }
  let currentUrl = url;
  let currentInit = init;
  for (const interceptor of requestInterceptors) {
    const out = await interceptor(currentUrl, currentInit);
    if (out) {
      currentUrl = out.url;
      currentInit = out.init ?? currentInit;
    }
  }
  return [currentUrl, currentInit];
}

export async function applyResponseInterceptors(response: Response): Promise<Response> {
  if (responseInterceptors.length === 0) {
    return response;
  }
  let current = response;
  for (const interceptor of responseInterceptors) {
    current = await interceptor(current);
  }
  return current;
}

/**
 * Maps failed `fetch` results to rejection reasons and performs logout on 401 when
 * the API body matches the legacy conditions.
 */
export async function processFetchError(err: unknown, reject: (reason?: unknown) => void): Promise<void> {
  console.log("Error in realFetch", err);
  const response = err as Response;
  if (typeof response.json !== "function") {
    reject(err);
    return;
  }
  try {
    const body = (await response.json()) as { errors?: { message?: string }[] };
    if (
      response.status === 401 &&
      authenticationService.currentUserValue &&
      (body.errors?.[0]?.message === "Token is expired" ||
        body.errors?.[0]?.message === "Invalid email or password")
    ) {
      reject(body);
      authenticationService.logout();
      return;
    }
    reject(body);
  } catch {
    reject(err);
  }
}
